import type { JSX } from 'react'

import { IconButton, SmileIcon, Tooltip } from '@buffer-mono/popcorn'

import { EmojiPicker } from '~publish/components/EmojiPicker'
import type { NoteReaction } from '~publish/hooks/notes/notesQueries'

import {
  describeNoteReaction,
  NOTE_REACTION_EMOJI_STORE,
} from './noteReactionDisplay'
import styles from './NoteReactions.module.css'

/**
 * The reaction groups under a Note. Each pill toggles the current account's
 * reaction and names who reacted; the trailing button adds another emoji.
 */
export function NoteReactionList({
  reactions,
  currentAccountId,
  canReact,
  onToggle,
}: {
  reactions: readonly NoteReaction[]
  currentAccountId: string | null
  canReact: boolean
  onToggle: (emoji: string) => void
}): JSX.Element {
  return (
    <div
      className={styles.list}
      data-empty={reactions.length === 0 || undefined}
    >
      {reactions.map((reaction) => (
        <Tooltip
          key={reaction.emoji}
          content={describeNoteReaction(reaction, currentAccountId)}
        >
          <button
            type="button"
            className={styles.pill}
            aria-pressed={reaction.hasReacted}
            aria-disabled={!canReact || undefined}
            aria-label={`${reaction.emoji} ${reaction.count} ${
              reaction.count === 1 ? 'reaction' : 'reactions'
            }`}
            onClick={() => {
              if (canReact) onToggle(reaction.emoji)
            }}
          >
            <span className={styles.emoji} aria-hidden="true">
              {reaction.emoji}
            </span>
            <span className={styles.count} aria-hidden="true">
              {reaction.count}
            </span>
          </button>
        </Tooltip>
      ))}
      {canReact && (
        <AddReactionButton variant="secondary" onSelect={onToggle} />
      )}
    </div>
  )
}

/** Opens the full emoji picker; the chosen emoji toggles like a pill. */
export function AddReactionButton({
  variant,
  onSelect,
  onOpenChange,
}: {
  variant: 'secondary' | 'tertiary'
  onSelect: (emoji: string) => void
  onOpenChange?: (open: boolean) => void
}): JSX.Element {
  return (
    <EmojiPicker
      onEmojiSelect={onSelect}
      onOpenChange={onOpenChange}
      frequencyStore={NOTE_REACTION_EMOJI_STORE}
      recordsSelections={false}
      side="bottom"
      align="end"
    >
      <IconButton
        label="Add reaction"
        size="small"
        type="button"
        variant={variant}
      >
        <SmileIcon size="xsmall" />
      </IconButton>
    </EmojiPicker>
  )
}

/** The account's most used reaction emoji, one tap each. */
export function QuickReactionButtons({
  emojis,
  onSelect,
}: {
  emojis: readonly string[]
  onSelect: (emoji: string) => void
}): JSX.Element {
  return (
    <>
      {emojis.map((emoji) => (
        <button
          key={emoji}
          type="button"
          className={styles.quickReaction}
          aria-label={`React with ${emoji}`}
          onClick={() => onSelect(emoji)}
        >
          <span aria-hidden="true">{emoji}</span>
        </button>
      ))}
    </>
  )
}
